export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, statusText: string, body: unknown) {
    super(apiErrorMessage(status, statusText, body))
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export const AUTHENTICATION_REQUIRED_EVENT = 'ams:authentication-required'

export function onAuthenticationRequired(handler: (event: Event) => void): () => void {
  if (typeof window === 'undefined') return () => {}

  window.addEventListener(AUTHENTICATION_REQUIRED_EVENT, handler)
  return () => window.removeEventListener(AUTHENTICATION_REQUIRED_EVENT, handler)
}

// Minimal JSON client for the v1 API. Same-origin: the SPA is served by Rails,
// so the HttpOnly session cookie rides along automatically (ADR 0001).
export async function getJSON<T>(path: string): Promise<T> {
  return requestJSON<T>(path)
}

export async function postJSON<T>(path: string, body: unknown): Promise<T> {
  return requestJSON<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function postFormData<T>(path: string, body: FormData): Promise<T> {
  return requestJSON<T>(path, {
    method: 'POST',
    body,
  })
}

export async function patchJSON<T>(path: string, body: unknown): Promise<T> {
  return requestJSON<T>(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function deleteJSON<T = void>(path: string, body?: unknown): Promise<T> {
  return requestJSON<T>(path, {
    method: 'DELETE',
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  })
}

export async function getText(path: string, signal?: AbortSignal): Promise<string> {
  const text = await requestJSON<string>(path, { headers: { Accept: 'text/csv' }, signal }, 'text')
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  return text
}

async function requestJSON<T>(
  path: string,
  init: RequestInit = {},
  format: 'json' | 'text' = 'json',
): Promise<T> {
  if (import.meta.env.VITE_DEMO_FAKES === 'true') {
    const { demoResponse } = await import('@/demo')
    const response = demoResponse(path, init)
    if (response !== undefined) return (await response) as T
  }

  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...init.headers,
    },
    credentials: 'same-origin',
  })

  if (res.ok && format === 'text') return (await res.text()) as T
  return resolveJSONResponse<T>(res, path)
}

// Shared HTTP semantics for real responses and the removable demo seam.
export async function resolveJSONResponse<T>(res: Response, path?: string): Promise<T> {
  const body = await responseBody(res)
  if (!res.ok) {
    if (res.status === 401) notifyAuthenticationRequired(path)
    throw new ApiError(res.status, res.statusText, body)
  }

  return body as T
}

async function responseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined

  const text = await res.text()
  if (text.length === 0) return undefined

  return JSON.parse(text) as unknown
}

function apiErrorMessage(status: number, statusText: string, body: unknown) {
  if (hasErrorMessage(body)) return body.error

  return statusText.length > 0 ? `${status} ${statusText}` : `${status}`
}

function hasErrorMessage(body: unknown): body is { error: string } {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  )
}

function notifyAuthenticationRequired(path?: string) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(new CustomEvent(AUTHENTICATION_REQUIRED_EVENT, { detail: { path } }))
}
